/**
 * Floating hover tooltips — rendered on document.body (position:fixed)
 * so they are never clipped by overflow:auto list containers.
 */
const TIP_ID = 'mexa-floating-tooltip';
const TRIGGER_SELECTOR = [
    '[data-tooltip]',
    '.role-dot',
    '.chat-clear-history-btn',
    '.chat-pipeline-debug-btn',
    '.btn-refresh-all',
    '.session-selector-new',
    '.navbar-help-btn',
].join(', ');
function ensureTipEl() {
    let tip = document.getElementById(TIP_ID);
    if (!tip) {
        tip = document.createElement('div');
        tip.id = TIP_ID;
        tip.className = 'mexa-floating-tooltip';
        tip.setAttribute('role', 'tooltip');
        tip.hidden = true;
        document.body.appendChild(tip);
    }
    return tip;
}
function readTipText(trigger) {
    var _a, _b, _c, _d;
    const data = (_a = trigger.getAttribute('data-tooltip')) === null || _a === void 0 ? void 0 : _a.trim();
    if (data)
        return data;
    const aria = (_b = trigger.getAttribute('aria-label')) === null || _b === void 0 ? void 0 : _b.trim();
    const title = (_c = trigger.getAttribute('title')) === null || _c === void 0 ? void 0 : _c.trim();
    const nested = trigger.querySelector('.mexa-tooltip, .chat-clear-history-tooltip, .chat-pipeline-debug-tooltip');
    const nestedText = (_d = nested === null || nested === void 0 ? void 0 : nested.textContent) === null || _d === void 0 ? void 0 : _d.trim();
    if (nestedText)
        return nestedText;
    return aria || title || '';
}
function placeTip(tip, trigger) {
    const rect = trigger.getBoundingClientRect();
    tip.hidden = false;
    tip.classList.add('is-visible');
    // Measure after visible so size is correct.
    const tipRect = tip.getBoundingClientRect();
    const gap = 8;
    let top = rect.bottom + gap;
    let left = rect.right - tipRect.width;
    // Keep inside the viewport.
    const pad = 8;
    if (left < pad)
        left = pad;
    if (left + tipRect.width > window.innerWidth - pad) {
        left = Math.max(pad, window.innerWidth - tipRect.width - pad);
    }
    if (top + tipRect.height > window.innerHeight - pad) {
        top = Math.max(pad, rect.top - tipRect.height - gap);
    }
    tip.style.top = `${Math.round(top)}px`;
    tip.style.left = `${Math.round(left)}px`;
}
function hideTip(tip) {
    tip.classList.remove('is-visible');
    tip.hidden = true;
    tip.textContent = '';
    tip.style.top = '';
    tip.style.left = '';
}
function resolveTrigger(target) {
    if (!(target instanceof Element))
        return null;
    return target.closest(TRIGGER_SELECTOR);
}
/** Strip native title so the browser balloon doesn't fight our tip. */
function suppressNativeTitle(el) {
    if (!(el instanceof HTMLElement))
        return;
    if (el.hasAttribute('title') && !el.dataset.mexaTitleSaved) {
        el.dataset.mexaTitleSaved = el.getAttribute('title') || '';
        el.removeAttribute('title');
    }
}
export function initMexaFloatingTooltips() {
    if (typeof document === 'undefined')
        return;
    if (window.__mexaFloatingTooltipsInit)
        return;
    window.__mexaFloatingTooltipsInit = true;
    const tip = ensureTipEl();
    let active = null;
    const showFor = (trigger) => {
        const text = readTipText(trigger);
        if (!text)
            return;
        suppressNativeTitle(trigger);
        active = trigger;
        tip.textContent = text;
        placeTip(tip, trigger);
    };
    const hide = () => {
        active = null;
        hideTip(tip);
    };
    document.addEventListener('pointerenter', (e) => {
        const trigger = resolveTrigger(e.target);
        if (!trigger)
            return;
        showFor(trigger);
    }, true);
    document.addEventListener('pointerleave', (e) => {
        const trigger = resolveTrigger(e.target);
        if (!trigger || trigger !== active)
            return;
        // Leaving into the tip itself shouldn't happen (pointer-events: none).
        hide();
    }, true);
    document.addEventListener('focusin', (e) => {
        const trigger = resolveTrigger(e.target);
        if (!trigger)
            return;
        showFor(trigger);
    }, true);
    document.addEventListener('focusout', (e) => {
        const trigger = resolveTrigger(e.target);
        if (!trigger || trigger !== active)
            return;
        hide();
    }, true);
    window.addEventListener('scroll', () => {
        if (active)
            hide();
    }, true);
    window.addEventListener('resize', () => {
        if (active)
            hide();
    });
}
